import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  SolarQuoteStepOrder,
  SolarQuoteSteps,
} from '@/constants/solarQuoteEcomm';
import type { Coordinates, SelectedAddress } from '@/types/models/address';
import type { MarketInfo } from '@/types/models/solarEcomm/marketInfo';

export enum OffsetSize {
  SMALL = 1,
  LARGE = 1.25,
}

export enum StorageSize {
  SMALL = 'LESS',
  LARGE = 'MORE',
}

export type SolarQuoteState = {
  selectedBatteryQuantity: number | undefined;
  selectedPanelConfigIndex: number | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  ownsHome: boolean;
  marketInfo: MarketInfo | undefined;
  ecommReferrerPage: string | undefined;
  selectedOffset: OffsetSize;
  selectedStorageSize: StorageSize;
  address: SelectedAddress | undefined;
  coordinates: Coordinates | undefined;
  currentStep: SolarQuoteSteps;

  // Ecomm intake
  avgMonthlyBill: number | undefined;
  availableUtilities: Array<string>;
  selectedUtility: string;
};

export const initialState: SolarQuoteState = {
  selectedPanelConfigIndex: undefined,
  selectedBatteryQuantity: undefined,
  firstName: undefined,
  lastName: undefined,
  phone: undefined,
  email: undefined,
  ownsHome: false,
  currentStep: SolarQuoteSteps.LEAD,
  marketInfo: undefined,
  ecommReferrerPage: undefined,
  selectedOffset: OffsetSize.SMALL,
  selectedStorageSize: StorageSize.SMALL,

  address: undefined,
  coordinates: undefined,

  // Ecomm intake
  avgMonthlyBill: undefined,
  availableUtilities: [],
  selectedUtility: '',
};

type ContactActionType = NonNullable<
  Pick<
    SolarQuoteState,
    'firstName' | 'lastName' | 'phone' | 'email' | 'ownsHome'
  >
>;

export const solarQuoteSlice = createSlice({
  name: 'SolarQuote',
  initialState,
  reducers: {
    incrementStep: (state) => {
      const newIndex = SolarQuoteStepOrder.indexOf(state.currentStep) + 1;
      const maxLength = SolarQuoteStepOrder.length;
      if (newIndex <= maxLength) {
        state.currentStep = SolarQuoteStepOrder[newIndex];
      } else {
        state.currentStep = SolarQuoteStepOrder[maxLength];
      }
    },
    decrementStep: (state) => {
      const newIndex = SolarQuoteStepOrder.indexOf(state.currentStep) - 1;
      if (newIndex >= 0) {
        state.currentStep = SolarQuoteStepOrder[newIndex];
      } else {
        state.currentStep = SolarQuoteStepOrder[0];
      }
    },
    setContactInformation: (
      state,
      action: PayloadAction<ContactActionType>
    ) => {
      const { firstName, lastName, phone, email } = action.payload;
      state.firstName = firstName;
      state.lastName = lastName;
      state.phone = phone;
      state.email = email;
    },
    setCoordinates: (state, action: PayloadAction<Coordinates | undefined>) => {
      state.coordinates = action.payload;
    },
    setAddress: (state, action: PayloadAction<SelectedAddress | undefined>) => {
      state.address = action.payload;
    },
    setUtility: (state, action: PayloadAction<{ utility: string }>) => {
      const { utility } = action.payload;
      state.selectedUtility = utility;
    },
    setSelectedBatteryQuantity: (
      state,
      action: PayloadAction<{ batteryQuantity: number | undefined }>
    ) => {
      const { batteryQuantity } = action.payload;
      state.selectedBatteryQuantity = batteryQuantity;
    },
    setSelectedPanelConfigIndex: (
      state,
      action: PayloadAction<{ panelConfigIndex: number | undefined }>
    ) => {
      const { panelConfigIndex } = action.payload;
      state.selectedPanelConfigIndex = panelConfigIndex;
    },
    setSelectedStorageSize: (
      state,
      action: PayloadAction<{ storageSize: StorageSize }>
    ) => {
      const { storageSize } = action.payload;
      state.selectedStorageSize = storageSize;
    },
    setSelectedOffset: (
      state,
      action: PayloadAction<{ offset: OffsetSize }>
    ) => {
      const { offset } = action.payload;
      state.selectedOffset = offset;
    },
    setEcommReferrerPage: (
      state,
      action: PayloadAction<{ ecommReferrerPage: string }>
    ) => {
      const { ecommReferrerPage } = action.payload;
      state.ecommReferrerPage = ecommReferrerPage;
    },
    setMarketInfo: (
      state,
      action: PayloadAction<{ marketInfo: MarketInfo }>
    ) => {
      const { marketInfo } = action.payload;
      state.marketInfo = marketInfo;
    },
    setAvgMonthlyBill: (state, action: PayloadAction<{ bill: number }>) => {
      const { bill } = action.payload;
      state.avgMonthlyBill = bill;
    },
    resetSolarQuote: () => {
      return initialState;
    },
  },
  selectors: {
    selectPanelConfigIndex: (sliceState) => sliceState.selectedPanelConfigIndex,
    selectBatteryQuantity: (sliceState) => sliceState.selectedBatteryQuantity,
    selectBasicUserInfo: createSelector(
      (sliceState: SolarQuoteState) => sliceState?.address?.state,
      (sliceState: SolarQuoteState) => sliceState.selectedUtility,
      (sliceState: SolarQuoteState) => sliceState.coordinates,
      (
        state: string | undefined,
        utility: string | undefined,
        coordinates: Coordinates | undefined
      ) => {
        if (!state || !utility || !coordinates) {
          return undefined;
        }

        return { state, utility, ...coordinates };
      }
    ),
    selectAvgMonthlyBill: (sliceState) => sliceState.avgMonthlyBill,
    selectOffset: (sliceState) => sliceState.selectedOffset,
    selectStorageSize: (sliceState) => sliceState.selectedStorageSize,
    selectEcommReferrerPage: (sliceState) => sliceState.ecommReferrerPage,
    selectAddress: (sliceState) => sliceState.address,
    selectCoordinates: (sliceState) => sliceState.coordinates,
    selectContactInformation: createSelector(
      (sliceState) => sliceState.firstName,
      (sliceState) => sliceState.lastName,
      (sliceState) => sliceState.phone,
      (sliceState) => sliceState.email,
      (
        firstName: string | undefined,
        lastName: string | undefined,
        phone: string | undefined,
        email: string | undefined
      ) => {
        if (!firstName || !lastName || !phone || !email) {
          return undefined;
        }

        return { firstName, lastName, phone, email };
      }
    ),
    selectMarketInfo: (sliceState) => sliceState.marketInfo,
  },
});

export const {
  incrementStep,
  decrementStep,
  setContactInformation,
  setCoordinates,
  setAddress,
  setUtility,
  setSelectedBatteryQuantity,
  setSelectedPanelConfigIndex,
  setAvgMonthlyBill,
  setSelectedOffset,
  setSelectedStorageSize,
  resetSolarQuote,
  setEcommReferrerPage,
  setMarketInfo,
} = solarQuoteSlice.actions;

export const {
  selectPanelConfigIndex,
  selectBatteryQuantity,
  selectBasicUserInfo,
  selectAvgMonthlyBill,
  selectOffset,
  selectStorageSize,
  selectEcommReferrerPage,
  selectAddress,
  selectCoordinates,
  selectContactInformation,
  selectMarketInfo,
} = solarQuoteSlice.selectors;

export default solarQuoteSlice.reducer;
export type SolarQuoteSlice = typeof solarQuoteSlice;
